import { Select } from 'client/components/core/Sidebar/components/Settings/components'
import MobileIconUi from 'client/components/core/Sidebar/components/Settings/styles/ui/MobileIconUi'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { stickyPositionLockKeys } from 'common/components/entities/Section/constants/StickyPositionLockKeys'
import { StickyPositionEnum } from 'common/components/entities/Section/enums/StickyPositionEnum'
import { StickyTypeEnum } from 'common/components/entities/Section/enums/StickyTypeEnum'
import { SectionInterface } from 'common/components/entities/Section/types/SectionInterface'
import React, { useEffect } from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import { useUpdateGlobalSettings } from '../../../hooks/useUpdateGlobalSetting'

interface StickySettingProps {
  sticky: SectionInterface['sticky']
  entityHtmlAttrId: string
  update: (value: SectionInterface['sticky']) => void
  title?: string
  label?: string
  tooltipText?: string
  isShowSelect?: boolean
  singleDeviceMode?: boolean
}

export const StickySetting = ({
  sticky,
  update,
  entityHtmlAttrId,
  title = 'settings_options.sticky.label',
  label = 'settings_options.sticky.description',
  tooltipText = 'settings_options.sticky.help_message',
  isShowSelect = true,
  singleDeviceMode = false,
}: StickySettingProps) => {
  const { isMobile } = useDeviceModeContext()
  const updateGlobalSettings = useUpdateGlobalSettings()

  const targetEntity = document.querySelector<HTMLElement>(
    `#${entityHtmlAttrId}`,
  )

  useEffect(() => {
    if (
      sticky?.position === StickyPositionEnum.top &&
      targetEntity?.offsetHeight
    ) {
      updateGlobalSettings({
        scrollPaddingTop: `${targetEntity.offsetHeight}px`,
      })
      return
    }
    updateGlobalSettings({ scrollPaddingTop: null })
  }, [entityHtmlAttrId, sticky, updateGlobalSettings, targetEntity])

  return (
    <>
      {title && <GroupTitle>{title}</GroupTitle>}
      <CheckboxControl
        value={!!sticky?.position}
        update={value =>
          update(
            value
              ? {
                  type: StickyTypeEnum.Sticky,
                  indent: 0,
                  position: StickyPositionEnum.top,
                }
              : { type: StickyTypeEnum.Relative }, // default position is relative: publisher/components/core/StyleWrapperUi.tsx
          )
        }
        label={label}
        tooltipText={tooltipText}
      />
      {!!sticky?.position && (
        <>
          {isShowSelect && (
            <Select
              value={sticky.position}
              update={(value: StickyPositionEnum) =>
                update({ ...sticky, position: value })
              }
              labelText="entity_settings.section.content_width.label"
              helpMessage="entity_settings.section.content_width.help_message"
              choiceList={stickyPositionLockKeys}
              labelIcon={isMobile && !singleDeviceMode && <MobileIconUi />}
            />
          )}
          <RangeElement
            value={sticky.indent}
            onChange={value => update({ ...sticky, indent: value })}
            labelIcon={isMobile && !singleDeviceMode ? <MobileIconUi /> : null}
            label="settings_options.sticky.indent"
            max={200}
          />
        </>
      )}
    </>
  )
}
